import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MdBedtime, MdRestaurant, MdWaterDrop, MdToys, MdTimer } from 'react-icons/md';
import Pill from './Pill';
import { useAppStore } from '../stores/appStore';
import { useValidatedBabyId } from '../hooks/useValidatedBabyId';
import { useNow } from '../hooks/useNow';
import { useOpenSleep } from '../../features/sleep/useOpenSleep';
import { formatDurationShort } from '../utils/dateHelpers';

/**
 * FloatingTimer — header pills for anything running right now.
 *
 * Two independent pills:
 *   - sleep: from useOpenSleep(babyId) (the open SLEEP event is the timer) → "Asleep · 42 m",
 *     live dot, tap → /sleep
 *   - activity/feed/pump: from the zustand activeTimer (FEED, FEED_DUAL, PUMPING, ACTIVITY)
 *     → "Feed · 14 m", tap → the feature route. SLEEP is no longer an activeTimer type.
 *
 * Minutes, not seconds; static dot with motion-safe:animate-pulseSoft; ≥ 44px hit box.
 */
const typeConfig = {
  FEED: { icon: MdRestaurant, route: '/feed', label: 'Feed', tone: 'feed' },
  FEED_DUAL: { icon: MdRestaurant, route: '/feed', label: 'Feed', tone: 'feed' },
  PUMPING: { icon: MdWaterDrop, route: '/pumping', label: 'Pump', tone: 'pump' },
  ACTIVITY: { icon: MdToys, route: '/activity', label: 'Activity', tone: 'activity' },
};

function activeTimerMinutes(activeTimer, nowMs) {
  const m = activeTimer.meta || {};
  const isDual = activeTimer.type === 'FEED_DUAL' || activeTimer.type === 'PUMPING';
  const startedAt = Number(activeTimer.startedAt) || nowMs;
  if (isDual) {
    // Dual-side timers carry accumulated per-side seconds in meta and only run the
    // clock for the currently-active side; show the aggregate session length.
    const baseSecs = (m.leftSeconds || 0) + (m.rightSeconds || 0);
    const liveSecs = m.activeSide ? Math.max(0, Math.floor((nowMs - startedAt) / 1000)) : 0;
    return Math.floor((baseSecs + liveSecs) / 60);
  }
  return Math.max(0, Math.floor((nowMs - startedAt) / 60000));
}

export default function FloatingTimer() {
  const activeTimer = useAppStore((s) => s.activeTimer);
  const navigate = useNavigate();
  const { selectedBabyId } = useValidatedBabyId();
  const sleep = useOpenSleep(selectedBabyId) || {};
  const openSleep = sleep.openSleep || null;
  const now = useNow(15000);

  const config = activeTimer ? typeConfig[activeTimer.type] : null;
  const showActivity = !!activeTimer && !!config;

  if (!openSleep && !showActivity) return null;

  const sleepMins = Math.max(0, Math.floor(Number(sleep.elapsedMinutes) || 0));
  const activityMins = showActivity ? activeTimerMinutes(activeTimer, now.getTime()) : 0;

  return (
    <div className="flex items-center gap-1.5">
      {openSleep && (
        <Pill
          as="button"
          tone="sleep"
          icon={MdBedtime}
          live
          onClick={() => navigate('/sleep')}
          aria-label={`Asleep for ${formatDurationShort(sleepMins)}. Open sleep`}
        >
          Asleep · {formatDurationShort(sleepMins)}
        </Pill>
      )}
      {showActivity && (
        <Pill
          as="button"
          tone={config.tone}
          icon={config.icon || MdTimer}
          live
          onClick={() => navigate(config.route)}
          aria-label={`${config.label} timer running for ${formatDurationShort(activityMins)}. Open ${config.label.toLowerCase()}`}
        >
          {config.label} · {formatDurationShort(activityMins)}
        </Pill>
      )}
    </div>
  );
}
